import { isLostResponseError } from '@/lib/api/error-utils'
import { expensesApi } from '@/lib/api/expenses.api'
import { getMutationErrorMessage } from '@/lib/api/mutation-feedback'
import { reconcileMutationQueries } from '@/lib/api/mutation-recovery'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
  CreateExpense,
  CreateExpenseDraft,
  Expense,
  ExpenseCategory,
  RecordExpenseReimbursement,
  UpdateExpense,
} from '@scrambl/shared'

import { queryKeys } from '../query-keys'
import { useOwnedMutation } from './useOwnedMutation'

const invalidateExpenseQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  expense?: Expense | null,
) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all })

  if (!expense) {
    return
  }

  queryClient.invalidateQueries({
    queryKey: queryKeys.expenses.detail(expense.id),
  })
  queryClient.invalidateQueries({
    queryKey: queryKeys.expenses.tabCounts(expense.id),
  })
}

export function useCreateExpenseMutation() {
  const queryClient = useQueryClient()

  return useOwnedMutation({
    mutationFn: (data: CreateExpense) => expensesApi.createExpense(data),
    onSuccess: expense => {
      invalidateExpenseQueries(queryClient, expense)
      toast.success('Expense draft created')
    },
    onError: async (error, _variables, _result, owner) => {
      if (isLostResponseError(error))
        await reconcileMutationQueries(queryClient, [queryKeys.expenses.all])
      await owner.assertOwnerCurrent()
      owner.assertOwnerCurrentSync()
      toast.error(getMutationErrorMessage(error, 'Failed to create expense'))
    },
  })
}

export function useCreateExpenseDraftMutation() {
  const queryClient = useQueryClient()

  return useOwnedMutation({
    mutationFn: (data: CreateExpenseDraft = {}) =>
      expensesApi.createExpenseDraft(data),
    onSuccess: expense => {
      invalidateExpenseQueries(queryClient, expense)
    },
    onError: async (error, _variables, _result, owner) => {
      if (isLostResponseError(error))
        await reconcileMutationQueries(queryClient, [queryKeys.expenses.all])
      await owner.assertOwnerCurrent()
      owner.assertOwnerCurrentSync()
      toast.error(
        getMutationErrorMessage(error, 'Failed to start expense draft'),
      )
    },
  })
}

export function useUpdateExpenseMutation() {
  const queryClient = useQueryClient()

  return useOwnedMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateExpense }) =>
      expensesApi.updateExpense(id, data),
    onSuccess: expense => {
      invalidateExpenseQueries(queryClient, expense)
      toast.success('Expense updated')
    },
    onError: async (error, _variables, _result, owner) => {
      if (isLostResponseError(error))
        await reconcileMutationQueries(queryClient, [queryKeys.expenses.all])
      await owner.assertOwnerCurrent()
      owner.assertOwnerCurrentSync()
      toast.error(getMutationErrorMessage(error, 'Failed to update expense'))
    },
  })
}

export function useDeleteExpenseMutation() {
  const queryClient = useQueryClient()

  return useOwnedMutation({
    mutationFn: (id: string) => expensesApi.deleteExpense(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all })
      toast.success('Expense deleted')
    },
    onError: async (error, _variables, _result, owner) => {
      if (isLostResponseError(error))
        await reconcileMutationQueries(queryClient, [queryKeys.expenses.all])
      await owner.assertOwnerCurrent()
      owner.assertOwnerCurrentSync()
      toast.error(getMutationErrorMessage(error, 'Failed to delete expense'))
    },
  })
}

function refreshFinancialExpenseState(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: queryKeys.expenses.all,
      predicate: query => query.queryKey[1] !== 'categories',
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.contractorInvoices.all,
      predicate: query => query.queryKey[1] !== 'profile',
    }),
  ])
}

function useExpenseActionMutation(
  mutationFn: (
    id: string,
    payload?: string | RecordExpenseReimbursement,
  ) => Promise<Expense>,
  successMessage: string,
  options?: { financial?: boolean },
) {
  const queryClient = useQueryClient()

  return useOwnedMutation({
    ...(options?.financial ? { retry: false } : {}),
    mutationFn: ({
      id,
      payload,
    }: {
      id: string
      payload?: string | RecordExpenseReimbursement
    }) => mutationFn(id, payload),
    onSuccess: async (expense, _variables, _result, owner) => {
      if (options?.financial) await refreshFinancialExpenseState(queryClient)
      else invalidateExpenseQueries(queryClient, expense)
      await owner.assertOwnerCurrent()
      owner.assertOwnerCurrentSync()
      toast.success(
        expense.financialCommand?.replayed
          ? 'Previous reimbursement found. Current balance refreshed.'
          : successMessage,
      )
    },
    onError: async (error, _variables, _result, owner) => {
      if (options?.financial && isLostResponseError(error)) {
        await refreshFinancialExpenseState(queryClient)
        await owner.assertOwnerCurrent()
        owner.assertOwnerCurrentSync()
        toast.error(
          'The reimbursement may have been recorded. Review the expense before trying again.',
        )
        return
      }
      if (isLostResponseError(error))
        await reconcileMutationQueries(queryClient, [queryKeys.expenses.all])
      await owner.assertOwnerCurrent()
      owner.assertOwnerCurrentSync()
      toast.error(getMutationErrorMessage(error, 'Expense action failed'))
    },
  })
}

export function useSubmitExpenseMutation() {
  return useExpenseActionMutation(
    id => expensesApi.submitExpense(id),
    'Expense submitted',
  )
}

export function useRecallExpenseMutation() {
  return useExpenseActionMutation(
    id => expensesApi.recallExpense(id),
    'Expense recalled',
  )
}

export function useApproveExpenseMutation() {
  return useExpenseActionMutation(
    id => expensesApi.approveExpense(id),
    'Expense approved',
  )
}

export function useRequestExpenseChangesMutation() {
  return useExpenseActionMutation(
    (id, payload) =>
      expensesApi.requestExpenseChanges(
        id,
        typeof payload === 'string' ? payload : undefined,
      ),
    'Changes requested',
  )
}

export function useRejectExpenseMutation() {
  return useExpenseActionMutation(
    (id, payload) =>
      expensesApi.rejectExpense(
        id,
        typeof payload === 'string' ? payload : undefined,
      ),
    'Expense rejected',
  )
}

export function useRecordExpenseReimbursementMutation() {
  return useExpenseActionMutation(
    (id, payload) =>
      expensesApi.recordReimbursement(
        id,
        payload as RecordExpenseReimbursement,
      ),
    'Reimbursement recorded',
    { financial: true },
  )
}

export function useCreateExpenseCategoryMutation() {
  const queryClient = useQueryClient()

  return useOwnedMutation({
    mutationFn: (data: Partial<ExpenseCategory> & { name: string }) =>
      expensesApi.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.expenses.categories(),
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all })
    },
    onError: async (error, _variables, _result, owner) => {
      if (isLostResponseError(error))
        await reconcileMutationQueries(queryClient, [queryKeys.expenses.all])
      await owner.assertOwnerCurrent()
      owner.assertOwnerCurrentSync()
      toast.error(
        getMutationErrorMessage(error, 'Failed to create expense category'),
      )
    },
  })
}

export function useUpdateExpenseCategoryMutation() {
  const queryClient = useQueryClient()

  return useOwnedMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Partial<ExpenseCategory>
    }) => expensesApi.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.expenses.categories(),
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all })
    },
    onError: async (error, _variables, _result, owner) => {
      if (isLostResponseError(error))
        await reconcileMutationQueries(queryClient, [queryKeys.expenses.all])
      await owner.assertOwnerCurrent()
      owner.assertOwnerCurrentSync()
      toast.error(
        getMutationErrorMessage(error, 'Failed to update expense category'),
      )
    },
  })
}

export function useDeleteExpenseCategoryMutation() {
  const queryClient = useQueryClient()

  return useOwnedMutation({
    mutationFn: (id: string) => expensesApi.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.expenses.categories(),
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all })
    },
    onError: async (error, _variables, _result, owner) => {
      if (isLostResponseError(error))
        await reconcileMutationQueries(queryClient, [queryKeys.expenses.all])
      await owner.assertOwnerCurrent()
      owner.assertOwnerCurrentSync()
      toast.error(
        getMutationErrorMessage(error, 'Failed to delete expense category'),
      )
    },
  })
}
